/**
 * ScenePreviewGallery
 *
 * Shows generated multi-scene mockups on a canvas product page.
 * Supports: multiple scenes, before/after toggle, mobile swipe, graceful fallback.
 * Only renders when scene data is available — zero visual footprint otherwise.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Eye, ChevronLeft, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function ScenePreviewGallery({ productId }) {
  const [scenes, setScenes]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [active, setActive]       = useState(0);
  const [showBefore, setShowBefore] = useState(false);
  const scrollRef = useRef(null);

  const fetchScenes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/products/scene-previews/${productId}`);
      const data = await res.json();
      setScenes(data.scenes || []);
    } catch (err) { console.error("[ScenePreviewGallery] fetch failed:", err); }
    setLoading(false);
  }, [productId]);

  useEffect(() => { if (productId) fetchScenes(); }, [productId, fetchScenes]);

  // Graceful fallback — render nothing if no scenes
  if (loading || scenes.length === 0) return null;

  const current   = scenes[active] || scenes[0];
  const hasBefore = !!current.before_url;
  const imageUrl  = showBefore && hasBefore ? current.before_url : current.composite_url;
  const fullUrl   = imageUrl.startsWith("/") ? `${API}${imageUrl}` : imageUrl;

  const scrollToThumb = (idx) => {
    const el = scrollRef.current?.children[idx];
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  const goTo = (idx) => {
    setActive(idx);
    setShowBefore(false);
    scrollToThumb(idx);
  };

  const prev = () => goTo((active - 1 + scenes.length) % scenes.length);
  const next = () => goTo((active + 1) % scenes.length);

  return (
    <section className="mt-16 mb-8" data-testid="scene-preview-gallery">
      {/* Section label */}
      <div className="flex items-center gap-3 mb-6">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-brand-gold/20 to-transparent" />
        <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold/70 shrink-0">
          See it in your space
        </p>
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-brand-gold/20 to-transparent" />
      </div>

      {/* Featured preview */}
      <div className="relative group rounded-sm overflow-hidden bg-black" data-testid="scene-featured">
        <img
          src={fullUrl}
          alt={current.scene_name || "Room preview"}
          className="w-full aspect-[16/9] md:aspect-[2.2/1] object-cover transition-opacity duration-500"
          onError={(e) => { e.target.src = ""; e.target.alt = ""; }}
        />

        {/* Scene name overlay */}
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-5 md:p-7">
          <p className="text-[11px] md:text-[12px] text-white/80 tracking-wide font-medium">
            {current.scene_name || `Scene ${active + 1}`}
          </p>
          {scenes.length > 1 && (
            <p className="text-[9px] text-white/30 mt-0.5">
              {active + 1} of {scenes.length} scenes
            </p>
          )}
        </div>

        {/* Before/After toggle */}
        {hasBefore && (
          <button
            onClick={() => setShowBefore((v) => !v)}
            className="absolute top-4 right-4 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm border border-white/10 text-white/80 text-[10px] tracking-wide font-medium px-3 py-1.5 rounded-full hover:bg-black/70 hover:border-white/20 transition-all"
            data-testid="before-after-toggle"
          >
            <Eye size={11} />
            {showBefore ? "Show with artwork" : "Show empty room"}
          </button>
        )}

        {/* Nav arrows (desktop) */}
        {scenes.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm border border-white/10 text-white/60 hover:text-white hover:bg-black/60 opacity-0 group-hover:opacity-100 transition-all"
              data-testid="scene-prev"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm border border-white/10 text-white/60 hover:text-white hover:bg-black/60 opacity-0 group-hover:opacity-100 transition-all"
              data-testid="scene-next"
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {scenes.length > 1 && (
        <div
          ref={scrollRef}
          className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide snap-x snap-mandatory"
          data-testid="scene-thumbnails"
        >
          {scenes.map((scene, i) => {
            const thumbUrl = (scene.composite_url || "").startsWith("/")
              ? `${API}${scene.composite_url}`
              : scene.composite_url;
            return (
              <button
                key={scene.group_id || `scene-${i}`}
                onClick={() => goTo(i)}
                className={`shrink-0 snap-start rounded-sm overflow-hidden border-2 transition-all duration-200 ${
                  i === active
                    ? "border-brand-gold w-20 h-14 md:w-24 md:h-16"
                    : "border-transparent opacity-60 hover:opacity-90 w-16 h-11 md:w-20 md:h-14"
                }`}
                data-testid={`scene-thumb-${i}`}
              >
                <img
                  src={thumbUrl}
                  alt={scene.scene_name || `Scene ${i + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
